
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { CheckCircle, XCircle, Upload, FileText } from 'lucide-react';
import { readExcelFile } from '@/utils/excel/core/fileReader';
import { parseSundayMarketPricesFromExcel } from '@/utils/excel/parsers/sundayMarketParser';
import { useBulkCreateSundayMarketPrices } from '@/hooks/use-sunday-market-prices';
import { CreateSundayMarketPriceInput } from '@/types/database/sunday-market-prices';
import SundayMarketPreviewTable from './SundayMarketPreviewTable';
import SundayMarketUploadTemplate from './SundayMarketUploadTemplate';
import { useToast } from '@/hooks/use-toast';

interface SundayMarketUploadProps {
  onSuccess: () => void;
}

const SundayMarketUpload: React.FC<SundayMarketUploadProps> = ({ onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<CreateSundayMarketPriceInput[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const bulkCreateSundayMarketPrices = useBulkCreateSundayMarketPrices();
  const { toast } = useToast();

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsProcessing(true);
    setParsedData([]);
    setErrors([]);

    try {
      const workbook = await readExcelFile(selectedFile);
      const { data, errors: parseErrors } = parseSundayMarketPricesFromExcel(workbook);
      
      setParsedData(data);
      setErrors(parseErrors);
      
      if (parseErrors.length > 0) {
        toast({
          title: "Parsing Issues",
          description: `Found ${parseErrors.length} issues while parsing the file. Please review them below.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "File Processed",
          description: `Successfully parsed ${data.length} records from the Excel file.`,
        });
      }
    } catch (error) {
      console.error('Error processing file:', error);
      setErrors([`Failed to process file: ${error instanceof Error ? error.message : 'Unknown error'}`]);
      toast({
        title: "Processing Error",
        description: "Failed to process the Excel file. Please check the file format.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (parsedData.length === 0) return;

    setIsProcessing(true);
    setUploadProgress(0);

    try {
      const batchSize = 50;
      const batches = [];
      
      for (let i = 0; i < parsedData.length; i += batchSize) {
        batches.push(parsedData.slice(i, i + batchSize));
      }

      let processed = 0;
      for (const batch of batches) {
        await bulkCreateSundayMarketPrices.mutateAsync(batch);
        processed += batch.length;
        setUploadProgress((processed / parsedData.length) * 100);
      }

      toast({
        title: "Upload Successful",
        description: `Successfully uploaded ${parsedData.length} Sunday market price records.`,
      });

      onSuccess();
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error instanceof Error ? error.message : 'Failed to upload data',
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setParsedData([]);
    setErrors([]);
    setUploadProgress(0);
  };

  return (
    <div className="space-y-6">
      <SundayMarketUploadTemplate />
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="file-upload">Select Excel File</Label>
          <Input
            id="file-upload"
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            disabled={isProcessing}
          />
        </div>

        {isProcessing && !uploadProgress && (
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>Processing Excel file...</AlertDescription>
          </Alert>
        )}

        {uploadProgress > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Uploading data...</span>
              <span>{Math.round(uploadProgress)}%</span>
            </div>
            <Progress value={uploadProgress} />
          </div>
        )}

        {errors.length > 0 && (
          <Alert variant="destructive">
            <XCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-1">
                <p className="font-medium">Found {errors.length} errors:</p>
                <ul className="list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                  {errors.slice(0, 10).map((error, index) => (
                    <li key={index} className="text-sm">{error}</li>
                  ))}
                  {errors.length > 10 && (
                    <li className="text-sm font-medium">...and {errors.length - 10} more errors</li>
                  )}
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {parsedData.length > 0 && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              Successfully parsed {parsedData.length} records. 
              {errors.length > 0 && ` ${errors.length} records had issues and were skipped.`}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {parsedData.length > 0 && (
        <SundayMarketPreviewTable data={parsedData} maxRows={10} />
      )}

      <div className="flex gap-3 justify-end">
        {(file || parsedData.length > 0) && (
          <Button variant="outline" onClick={resetUpload} disabled={isProcessing}>
            Reset
          </Button>
        )}
        
        {parsedData.length > 0 && errors.length === 0 && (
          <Button 
            onClick={handleUpload}
            disabled={isProcessing || bulkCreateSundayMarketPrices.isPending}
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload {parsedData.length} Records
          </Button>
        )}
      </div>
    </div>
  );
};

export default SundayMarketUpload;
